import React, { FC, useState } from "react";
import Box from "react-raster";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { inject, observer } from "mobx-react";
import { RootStore, AuthStore } from "src/stores";
import { FeedbackType, FileData, FileType } from "src/types";
import { File } from "src/models";
import { Modal, Heading, Button, Radio, Spacer, Files, Text, TextArea } from "src/components";
import toast from 'react-hot-toast';

const FeedbackText = styled(Text)`
white-space: inherit;
`

const StyledFeedback = styled.div`
  align-items:     left;
  display:         flex;
  flex-direction:  column;
  flex:            0 0 100%;
  justify-content: left;
  margin-top:      1rem;
`;

const FeedbackWrapper = styled.div`
  padding: 2rem 2rem;
`;

const ButtonWrapper = styled.div`
  display:         flex;
  flex-direction:  row;
  justify-content: flex-end;
  margin-top:      1rem;
`;

const RadioButtonWrapper = styled.div`
  align-content:  flex-start;
  display:        flex;
  flex-direction: row;
  gap:            2rem;
  margin:         1rem 0 1rem 0;
`;

type FeedBackProps = {
  rootStore?: RootStore;
  authStore?: AuthStore;
};

export const Feedback: FC<FeedBackProps> = inject(
  "rootStore",
  "authStore"
)(
  observer((props: FeedBackProps): JSX.Element => {

    const { t, i18n } = useTranslation();

    const types: FeedbackType[] = [t("idea"), t("support"), t("bug_report")];

    const [type,    setType]    = useState<FeedbackType>(types[0]);
    const [message, setMessage] = useState<string>('');
    const [sent,    setSent]    = useState<boolean>();
    const [sending, setSending] = useState<boolean>(false);
    const [files,   setFiles]   = useState<File[]>([]);

    const discard = async () => {
      props.rootStore.toggleFeedbackModal(false);
    };

    const submit = async () => {
      setSending(true);
      const success = await props.rootStore.submitFeedback({
        type,
        message,
        files
      });
      setSent(true);
      if( success ) {
        toast.success(t("thanks_for_your_feedback"));
      } else {
        toast.error(t("error_while_sending_feedback"));
      }
      setType(types[0]);
      setFiles([]);
      setMessage('');
      props.rootStore.toggleFeedbackModal(false);
      setSending(false);
    };

    const fileAdded = (fileData: FileData) => {
      setFiles(currentFiles => [...currentFiles, File.create({...fileData, workspace_id: props.authStore.getCurWorkspaceID}) ]);
    };

    const fileRemoved = (file: FileData | File) => {
      setFiles(currentFiles => currentFiles.filter(f => f.id != file.id) );
    };

    if (!props.rootStore.showFeedbackModal) return null;

    return (
      <Modal
        show    = {true}
        onClose = {() => props.rootStore.toggleFeedbackModal(false)}
      >
        <FeedbackWrapper>
          <Heading type="h3">{t("leave_feedback")}</Heading>
          <Spacer />
          <StyledFeedback>
              <Box colspan={1}>
                <FeedbackText size="small">{t("feedback_modal_info")}</FeedbackText>
                <RadioButtonWrapper>
                  {types.map((x) => (
                    <Radio
                      checked = {type == x}
                      key     = {x}
                      label   = {t(x)}
                      onClick = {setType}
                      value   = {x}
                    />
                  ))}
                </RadioButtonWrapper>
                <Box margin='1rem 0rem'>
                <TextArea
                    defaultValue = {message}
                    onChange     = {(text) => setMessage(text)}
                    rows         = {5}
                    maxLength    = {200}
                    label        = {t("message")}
                  />
                </Box>
                <Files
                  files      = {files}
                  onDelete   = {(file) => fileRemoved(file)}
                  onUpload   = {(file) => fileAdded(file)}
                  type       = {FileType.Attachment}
                />
                <ButtonWrapper>
                  <Button isLoading={sending} onClick={submit} disabled={!message}>{t("send")}</Button>
                  <Spacer />
                  <Button variant="outlined" onClick={discard}>
                    {t("cancel")}
                  </Button>
                </ButtonWrapper>
              </Box>
          </StyledFeedback>
        </FeedbackWrapper>
      </Modal>
    );
  })
);
